"use client"
import { useState } from "react"
import "aos/dist/aos.css"

const ContactForm = () => {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [jobTitle, setJobTitle] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [message, setMessage] = useState("")
  const [submitMessage, setSubmitMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Simple sanitization helpers
  const sanitizeText = (str: string) => str.replace(/[<>]/g, "").trim()

  const clearForm = () => {
    setFirstName("")
    setLastName("")
    setJobTitle("")
    setEmail("")
    setPhone("")
    setMessage("")
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitMessage(null)

    // Basic client-side validation
    if (!firstName || !lastName || !jobTitle || !email || !phone || !message) {
      setSubmitMessage("Please fill in all required fields.")
      return
    }

    // Email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubmitMessage("Please enter a valid email address.")
      return
    }

    setSubmitting(true)
    const formData = new FormData()
    formData.append("first_name", sanitizeText(firstName))
    formData.append("last_name", sanitizeText(lastName))
    formData.append("job_title", sanitizeText(jobTitle))
    formData.append("email", sanitizeText(email))
    formData.append("phone", sanitizeText(phone))
    formData.append("message", sanitizeText(message))

    try {
      const res = await fetch("/api/contact-submit.php", {
        method: "POST",
        body: formData,
      })
      const text = await res.text()
      if (res.ok && text.includes("Mail sent successfully")) {
        setSubmitMessage("Message sent successfully!")
        clearForm()
      } else {
        setSubmitMessage("Submission failed. Please try again later.")
      }
    } catch {
      setSubmitMessage("Submission failed. Please try again later.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      className="w-full bg-white  p-4 xl:p-16 flex flex-col gap-6 border border-[--color-gray]  text-[16px] mb-4"
      onSubmit={handleSubmit}
      data-aos="fade-up"
      data-aos-duration="1000"
      autoComplete="off"
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label
            htmlFor="firstName"
            className="block  lg:text-[24px] text-gray-700 mb-2"
          >
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full border xl:text-[24px]  border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
          />
        </div>
        <div className="flex-1">
          <label
            htmlFor="lastName"
            className="block  lg:text-[24px] text-gray-700 mb-2"
          >
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
          />
        </div>
      </div>
      <div>
        <label
          htmlFor="jobTitle"
          className="block  lg:text-[24px] text-gray-700 mb-2"
        >
          Company Job Title
        </label>
        <input
          type="text"
          id="jobTitle"
          name="jobTitle"
          required
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>
      <div>
        <label
          htmlFor="email"
          className="block  lg:text-[24px] text-gray-700 mb-2"
        >
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>
      <div>
        <label
          htmlFor="phone"
          className="block  lg:text-[24px] text-gray-700 mb-2"
        >
          Phone
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>
      <div>
        <label
          htmlFor="message"
          className="block  lg:text-[24px] text-gray-700 mb-2"
        >
          Message
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full border xl:text-[24px] border-gray-300  px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
        />
      </div>
      <div>
        <button
          type="submit"
          className="bg-[--color-brand] hover:bg-[--color-danger] uppercase font-oswald text-[--color-dark]  text-white flex w-full xl:w-fit items-center justify-center gap-4 text-[16px] 2  3xl:text-[38px]  py-5 px-5 3xl:px-16 transition-all duration-300 ease-in-out"
          disabled={submitting}
        >
          {submitting ? "Sending..." : "Send Message"}
        </button>
      </div>
      {submitMessage && (
        <div
          className="mt-4 p-3 rounded bg-gray-100 text-gray-800 border border-gray-300 cursor-pointer"
          onClick={() => setSubmitMessage(null)}
        >
          {submitMessage}
        </div>
      )}
    </form>
  )
}

export default ContactForm
